import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { useMatchMedia } from '@betterment/js-runtime';
import { Box, H3, Separator, Text } from '@betterment/samba-react';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { PurposeHeaderDocument } from './graphql/purposeHeader.generated';

import {
  DepositButton,
  OtherTransactionOptionsMenu,
} from '../../../Shared/components/transaction-buttons/TransactionButtons';
import { SettingsModal } from '../settings-modal/SettingsModal';

export const Header: React.FC = () => {
  const purposeId = useRequiredParam('purposeId');
  const isAtLeastMedium = useMatchMedia('atLeastMedium');
  const isAtLeastLarge = useMatchMedia('atLeastLarge');

  const { data } = useGraphQLQuery(PurposeHeaderDocument, { variables: { id: purposeId }, throwOnError: true });
  if (!data) return null;

  const { purpose } = data;
  const { balance, transactionOptions, legalSubAccounts } = purpose.envelope;

  const subAccounts = legalSubAccounts.map(sa => ({
    id: sa.legacySubAccountId,
    taxationType: sa.legalAccount.taxationType,
  }));

  return (
    <Box
      css={{
        paddingY: {
          base: 'space.200',
          md: 'space.400',
        },
        paddingX: { base: 'space.300', md: 'space.0' },
        md: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
        mdDown: { display: 'grid', gap: 'space.300' },
      }}
    >
      <Box css={{ display: 'flex', gap: 'space.200' }}>
        <ThemedImage
          themedAsset={purpose.image}
          alt={purpose.name}
          css={{
            width: { base: 'size.800', md: 'size.900' },
            height: { base: 'size.800', md: 'size.900' },
          }}
        />
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <H3 variant="title200">{purpose.name}</H3>
            {balance && (
              <Text variant="text400" css={{ fontSize: { base: 'fontSize.300', md: 'fontSize.400' } }}>
                {formatMoney(balance)}
              </Text>
            )}
          </Box>
          {!isAtLeastLarge && (
            <Box css={{ position: 'absolute', right: '0', top: 'space.150' }}>
              <SettingsModal showFullHeader={false} />
            </Box>
          )}
        </Box>
      </Box>
      <Separator css={{ display: { base: 'block', md: 'none' } }} />
      <Box
        css={{
          display: 'flex',
          gap: 'space.200',
          justifyContent: { base: 'space-between', md: 'flex-start' },
        }}
      >
        {isAtLeastLarge && <SettingsModal />}
        <OtherTransactionOptionsMenu
          transactionOptions={transactionOptions}
          subAccounts={subAccounts}
          isCompact={!isAtLeastMedium}
        />
        <DepositButton transactionOptions={transactionOptions} subAccounts={subAccounts} isCompact={!isAtLeastMedium} />
      </Box>
    </Box>
  );
};
